import { Loading } from '@/components/loading/loading.component';
import { Body3 } from '@/components/typography/body/bodies.component';
import { H3 } from '@/components/typography/heading/headings.component';
import { TableBody, TableBorderless, TableCell, TableHead, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useSoilPyramidData } from '@/modules/Report/Report.api';
import { toast } from 'react-toastify';

type InfoTableData = {
  soilTexture: string;
  soilType: string;
  soilTextureDepth: string;
};

const InfoTableSoilPyramid = ({ soilTexture, soilType, soilTextureDepth }: InfoTableData) => {
  return (
    <TableBorderless className={cn('border-b-2 border-secondary')}>
      <TableBody>
        {soilType && (
          <TableRow>
            <TableHead className="w-1/2">Soil Type</TableHead>
            <TableCell className="text-right">{soilType}</TableCell>
          </TableRow>
        )}
        {soilTexture && (
          <TableRow>
            <TableHead className="w-1/2">Soil Texture (mineral component)</TableHead>
            <TableCell className="text-right">{soilTexture}</TableCell>
          </TableRow>
        )}
        {soilTextureDepth && (
          <TableRow>
            <TableHead className="w-1/2">Soil Texture Depths</TableHead>
            <TableCell className="text-right">{soilTextureDepth}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </TableBorderless>
  );
};

type ScaleKeyData = {
  sand: number;
  silt: number;
  clay: number;
};

const SoilPyramidScale = ({ sand, silt, clay }: ScaleKeyData) => {
  const legendItems = [
    { name: 'Sand', colorClass: 'bg-soilPyramid-Sa' },
    { name: 'Silt', colorClass: 'bg-soilPyramid-SiLo' },
    { name: 'Clay', colorClass: 'bg-soilPyramid-Cl' },
  ];

  return (
    <div className={cn('flex items-center gap-4 sm:gap-6 justify-center -ml-5')}>
      {/* Main chart container - constrained width to leave room for legend */}
      <div className={cn('flex-1 max-w-4xl')}>
        <div className={cn('flex flex-row')}>
          <div style={{ width: `${sand}%` }} className={cn('border h-8 bg-soilPyramid-Sa content-center')}>
            <Body3 className={cn('text-center text-neutral-0')}>{sand}%</Body3>
          </div>
          <div style={{ width: `${silt}%` }} className={cn('border h-8 bg-soilPyramid-SiLo content-center')}>
            <Body3 className={cn('text-center text-neutral-0')}>{silt}%</Body3>
          </div>
          <div className={cn('flex-grow border h-8 bg-soilPyramid-Cl content-center')}>
            <Body3 className={cn('text-center text-neutral-0')}>{clay}%</Body3>
          </div>
        </div>
      </div>

      {/* Legend positioned to the right with border - responsive sizing */}
      <div className={cn('flex-shrink-0')}>
        <div className={cn('border-2 border-primary rounded p-1 sm:p-2 bg-white')}>
          <div className={cn('flex flex-col gap-0.5 sm:gap-1')}>
            {legendItems.map((item) => (
              <div key={item.name} className={cn('flex flex-row gap-1 sm:gap-2 items-center')}>
                <div className={cn('h-2.5 w-2.5 sm:h-3.5 sm:w-3.5', item.colorClass)}></div>
                <Body3 className={cn('text-[11px] sm:text-xs pr-2')}>{item.name}</Body3>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const SoilPyramid = ({ fieldId }: { fieldId: string }) => {
  const { data: soilPyramidData, status } = useSoilPyramidData(fieldId);
  if (status === 'pending') return <Loading />;
  if (status === 'error') {
    toast.error('Failed to fetch soil triangle data');
    return null;
  }
  const isDisplayingSoilInfo = soilPyramidData.soilTexture || soilPyramidData.soilType || soilPyramidData.soilTextureDepth;
  const hasValidSoilComposition = Math.abs(soilPyramidData.sand + soilPyramidData.silt + soilPyramidData.clay - 100) < 0.02;
  const isDisplayingPyramidKey = hasValidSoilComposition;
  const isDisplayingPyramid = soilPyramidData.pyramid && isDisplayingPyramidKey;
  if (!isDisplayingPyramid && !isDisplayingSoilInfo) {
    return null;
  }
  return (
    <div className="shadow p-7 mb-6">
      {isDisplayingSoilInfo && (
        <>
          <H3 className={cn('mb-4')}>Field Soil Information</H3>
          <InfoTableSoilPyramid
            soilTexture={soilPyramidData.soilTexture}
            soilType={soilPyramidData.soilType}
            soilTextureDepth={soilPyramidData.soilTextureDepth}
          />
        </>
      )}
      {isDisplayingPyramid && (
        <>
          <div className={cn('pt-8 flex flex-col gap-2')}>
            <H3>Soil Physical Analysis</H3>
            <Body3>
              If Soil Type above is Organic or Organo-Mineral, then analysis is for the mineral component only. Note that there may be
              variation in soil texture across the field, and that results shown below are an average based on a composite sample.
            </Body3>
          </div>

          {soilPyramidData.pyramid && (
            <div className={cn('flex justify-center w-full mt-8 ml-5')}>
              <img src={`data:image/png;base64,${soilPyramidData.pyramid}`} alt="Soil Texture Pyramid" className={'max-w-2xl h-auto'} />
            </div>
          )}
          {isDisplayingPyramidKey && (
            <div className={cn('mt-4')}>
              <SoilPyramidScale sand={soilPyramidData.sand} silt={soilPyramidData.silt} clay={soilPyramidData.clay} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
